// Projects list order (v4.23.9, Karan 30/10/2026: "why is this not recent to
// old?"). The list now reads newest first — project number descending — on every
// tab. Late projects keep their orange due date and dot, so they still stand out
// without being hoisted to the top. The column headers No., Project, Stage, Lead
// and Due sort the list; a click toggles the direction. Due puts blank dates
// last whichever way it runs. Each person's choice is remembered in this
// browser (localStorage, keyed by who is signed in), like Files remembers its sort.
//
// Pure: no DOM, no Firestore. The view passes the lookups (lead name, due date).

export const SORT_KEYS = ['number', 'client', 'stage', 'lead', 'due'];
export const DEFAULT_SORT = Object.freeze({ key: 'number', dir: 'desc' });
// The direction a column starts in when first clicked.
export const FIRST_DIR = { number: 'desc', client: 'asc', stage: 'asc', lead: 'asc', due: 'asc' };
const STAGE_ORDER = ['Not Started', 'In Progress', 'Review', 'Ready to Issue', 'Part Issued', 'Issued', 'Complete'];

export const isDefaultSort = (s) => !!s && s.key === DEFAULT_SORT.key && s.dir === DEFAULT_SORT.dir;

/** Click on a header: the same column flips, a new column starts in its first direction. */
export function nextSort(current, key) {
  if (!SORT_KEYS.includes(key)) return current || { ...DEFAULT_SORT };
  if (current && current.key === key) return { key, dir: current.dir === 'asc' ? 'desc' : 'asc' };
  return { key, dir: FIRST_DIR[key] };
}

const byNumberDesc = (a, b) => String(b.number || '').localeCompare(String(a.number || ''), 'en-GB', { numeric: true });
const text = (v) => String(v || '').trim();

/**
 * A comparator for { key, dir }. Ties fall back to newest first; anything still
 * equal keeps its incoming order (Array.prototype.sort is stable).
 *   leadName(p) -> display name ('' when none); dueOf(p) -> Date | null.
 */
export function projectComparator(sort = DEFAULT_SORT, { leadName = () => '', dueOf = () => null } = {}) {
  const key = SORT_KEYS.includes(sort && sort.key) ? sort.key : DEFAULT_SORT.key;
  const sign = (sort && sort.dir) === 'asc' ? 1 : -1;
  if (key === 'number') return (a, b) => -sign * byNumberDesc(a, b);
  // Blanks last whichever way the column runs, then the column, then newest first.
  const blanksLast = (va, vb, cmp) => {
    const ea = va == null || va === '', eb = vb == null || vb === '';
    if (ea || eb) return ea === eb ? 0 : ea ? 1 : -1;
    return sign * cmp(va, vb);
  };
  const pick = {
    client: (p) => text(p.clientName).toLowerCase(),
    lead: (p) => text(leadName(p)).toLowerCase(),
    stage: (p) => { const i = STAGE_ORDER.indexOf(p.status); return i < 0 ? (p.status ? STAGE_ORDER.length : null) : i; },
    due: (p) => { const d = dueOf(p); const t = d && d.getTime ? d.getTime() : NaN; return Number.isFinite(t) ? t : null; },
  }[key];
  const cmp = key === 'stage' || key === 'due' ? (x, y) => x - y : (x, y) => x.localeCompare(y, 'en-GB');
  return (a, b) => blanksLast(pick(a), pick(b), cmp) || byNumberDesc(a, b);
}

// ---- Remembered sort ------------------------------------------------------
export const sortStorageKey = (who) => `pj4-sort:${String(who || 'anon').toLowerCase()}`;

/** The person's saved sort, or newest first. Never throws (private windows). */
export function readSort(storage, who) {
  try {
    const v = JSON.parse(storage.getItem(sortStorageKey(who)) || 'null');
    if (v && SORT_KEYS.includes(v.key) && (v.dir === 'asc' || v.dir === 'desc')) return { key: v.key, dir: v.dir };
  } catch (_) { /* no storage, or junk: fall through */ }
  return { ...DEFAULT_SORT };
}

/** Save it; newest first clears the entry so the default stays the default. */
export function writeSort(storage, who, sort) {
  try {
    if (!sort || isDefaultSort(sort)) storage.removeItem(sortStorageKey(who));
    else storage.setItem(sortStorageKey(who), JSON.stringify({ key: sort.key, dir: sort.dir }));
  } catch (_) { /* private mode: the sort lasts this visit only */ }
}
